import { useEffect, useState, useRef } from "react";
import logo from "../assets/logoImage.webp";
import logo2x from "../assets/logoImage@2x.webp";

// ─── Inline circuit SVG (matches project's ScrollCircuitLines style) ─────────
const CIRCUIT_SVG = `<svg xmlns='http://www.w3.org/2000/svg' width='120' height='2500' viewBox='0 0 120 2500'><g fill='none' stroke='currentColor' stroke-width='1.5'><line x1='30' y1='0' x2='30' y2='2500' opacity='0.35'/><line x1='70' y1='0' x2='70' y2='2500' opacity='0.18' stroke-dasharray='5,10'/><line x1='100' y1='0' x2='100' y2='2500' opacity='0.1'/><path d='M30,150 L50,150 L60,160 L60,250'/><circle cx='60' cy='250' r='3' fill='currentColor'/><path d='M30,180 L15,180 L15,220'/><circle cx='15' cy='220' r='2' fill='currentColor'/><path d='M70,450 L90,450 L90,520'/><circle cx='90' cy='520' r='3' fill='currentColor'/><path d='M30,480 L50,480 L50,550'/><circle cx='50' cy='550' r='4' fill='currentColor'/><rect x='10' y='400' width='6' height='6' opacity='0.5'/><path d='M30,850 L20,850 L20,900'/><circle cx='20' cy='900' r='2.5' fill='currentColor'/><path d='M70,920 L50,920 L50,960'/><circle cx='50' cy='960' r='3' fill='currentColor'/><path d='M100,880 L80,880 L80,940'/><circle cx='80' cy='940' r='2' fill='currentColor'/><path d='M30,1250 L60,1250 L80,1270 L80,1350'/><circle cx='80' cy='1350' r='3.5' fill='currentColor'/><path d='M70,1300 L90,1300 L90,1380'/><circle cx='90' cy='1380' r='2' fill='currentColor'/><rect x='15' y='1280' width='8' height='8' opacity='0.5'/><path d='M30,1650 L15,1650 L15,1720'/><circle cx='15' cy='1720' r='3' fill='currentColor'/><path d='M70,1680 L50,1680 L50,1750'/><circle cx='50' cy='1750' r='2.5' fill='currentColor'/></g></svg>`;
const CIRCUIT_URL = `data:image/svg+xml,${encodeURIComponent(CIRCUIT_SVG)}`;

// ─── Duration config ──────────────────────────────────────────────────────────
const FILL_DURATION   = 2800;  // ms – how long the bar takes to fill to 100%
const EXIT_DELAY      = 320;   // ms – pause at 100% before exit starts
const EXIT_DURATION   = 700;   // ms – duration of the exit animation (CSS)

// ─── Traveling neon dot on circuit rail ──────────────────────────────────────
function TravelDot({ color, dur, delay, side }) {
  return (
    <span style={{
      position: "absolute",
      top: 0,
      [side]: side === "left" ? 26 : 26,
      width: 7, height: 7,
      borderRadius: "50%",
      backgroundColor: color,
      boxShadow: `0 0 10px 3px ${color}`,
      animation: `ld-travel ${dur}s ${delay}s linear infinite`,
      zIndex: 3,
    }} />
  );
}

export default function LoadingScreen() {
  const isArabic = document.documentElement.lang === "ar" || !document.documentElement.lang;

  // progress: 0–100
  const [progress, setProgress] = useState(0);
  // exiting: triggers CSS exit animation
  const [exiting, setExiting] = useState(false);
  // hidden: removes from DOM after exit animation
  const [hidden, setHidden] = useState(false);

  const startRef = useRef(null);
  const rafRef   = useRef(null);

  useEffect(() => {
    startRef.current = performance.now();

    const tick = (now) => {
      const elapsed = now - startRef.current;
      const pct = Math.min((elapsed / FILL_DURATION) * 100, 100);
      setProgress(pct);

      if (pct < 100) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        // Pause at 100%, then start exit
        setTimeout(() => {
          setExiting(true);
          setTimeout(() => setHidden(true), EXIT_DURATION);
        }, EXIT_DELAY);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  if (hidden) return null;

  const pct = Math.round(progress);

  return (
    <>
      <style>{`
        /* ── Traveling dot ────────────────────────────── */
        @keyframes ld-travel {
          0%   { transform:translateY(-16px); opacity:0; }
          6%   { opacity:1; }
          94%  { opacity:1; }
          100% { transform:translateY(100vh); opacity:0; }
        }

        /* ── Logo float ──────────────────────────────── */
        @keyframes ld-float {
          0%,100% { transform:translateY(0); filter:drop-shadow(0 0 12px rgba(56,189,248,.6)); }
          50%      { transform:translateY(-9px); filter:drop-shadow(0 0 26px rgba(56,189,248,1)); }
        }

        /* ── Spinner rings ───────────────────────────── */
        @keyframes ld-cw  { to { transform:rotate(360deg);  } }
        @keyframes ld-ccw { to { transform:rotate(-360deg); } }

        /* ── Progress glow pulse ─────────────────────── */
        @keyframes ld-glow {
          0%,100% { opacity:.7; }
          50%      { opacity:1; }
        }

        /* ── Scanline sweep ──────────────────────────── */
        @keyframes ld-scan {
          from { transform:translateY(-100%); }
          to   { transform:translateY(100vh); }
        }

        /* ── Card entrance ───────────────────────────── */
        @keyframes ld-in {
          from { opacity:0; transform:translateY(24px) scale(.97); }
          to   { opacity:1; transform:translateY(0)    scale(1); }
        }

        /* ── Exit: whole screen collapses + fades ────── */
        @keyframes ld-exit {
          0%   { opacity:1; transform:scale(1); }
          40%  { opacity:1; transform:scale(1.03); }
          100% { opacity:0; transform:scale(.94); }
        }

        .ld-exiting {
          animation: ld-exit ${EXIT_DURATION}ms cubic-bezier(.4,0,.2,1) forwards !important;
        }
      `}</style>

      {/* ── Root ─────────────────────────────────────────────────────────── */}
      <div
        className={exiting ? "ld-exiting" : ""}
        style={{
          position: "fixed", inset: 0, zIndex: 9999,
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          overflow: "hidden",
          background: "linear-gradient(135deg,#070f1e 0%,#0d1b2e 50%,#070f1e 100%)",
        }}
      >

        {/* ── Ambient orbs ────────────────────────────────────────────── */}
        <div style={{
          position:"absolute", top:"12%", left:"18%",
          width:480, height:480, borderRadius:"50%", pointerEvents:"none",
          background:"radial-gradient(circle,rgba(13,110,253,.13) 0%,transparent 70%)",
        }}/>
        <div style={{
          position:"absolute", bottom:"8%", right:"12%",
          width:380, height:380, borderRadius:"50%", pointerEvents:"none",
          background:"radial-gradient(circle,rgba(250,166,23,.11) 0%,transparent 70%)",
        }}/>

        {/* ── Scanline ────────────────────────────────────────────────── */}
        <div style={{
          position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden", zIndex:1,
        }}>
          <div style={{
            position:"absolute", left:0, right:0, height:2,
            background:"linear-gradient(to right,transparent,rgba(56,189,248,.18),transparent)",
            animation:"ld-scan 4.5s linear infinite",
          }}/>
        </div>

        {/* ── Left circuit rail ───────────────────────────────────────── */}
        <div style={{
          position:"absolute", top:0, left:0, width:120, height:"100%",
          color:"#38BDF8", opacity:.5, pointerEvents:"none", zIndex:2,
          backgroundImage:`url("${CIRCUIT_URL}")`,
          backgroundRepeat:"repeat-y", backgroundSize:"120px 2500px",
          filter:"drop-shadow(0 0 6px rgba(56,189,248,.7))",
        }}/>
        <TravelDot color="#38BDF8" dur={3.2} delay={0}   side="left" />
        <TravelDot color="#38BDF8" dur={4}   delay={1.4} side="left" />
        <TravelDot color="#38BDF8" dur={2.7} delay={2.6} side="left" />

        {/* ── Right circuit rail ──────────────────────────────────────── */}
        <div style={{
          position:"absolute", top:0, right:0, width:120, height:"100%",
          color:"#FAA617", opacity:.5, pointerEvents:"none", zIndex:2,
          backgroundImage:`url("${CIRCUIT_URL}")`,
          backgroundRepeat:"repeat-y", backgroundSize:"120px 2500px",
          filter:"drop-shadow(0 0 6px rgba(250,166,23,.7))",
          transform:"scaleX(-1)",
        }}/>
        <TravelDot color="#FAA617" dur={3.5} delay={0.7} side="right" />
        <TravelDot color="#FAA617" dur={2.9} delay={2}   side="right" />
        <TravelDot color="#FAA617" dur={4.2} delay={0.3} side="right" />

        {/* ── Glass card ──────────────────────────────────────────────── */}
        <div style={{
          position:"relative", zIndex:10,
          padding:"2.8rem 3.5rem 2.4rem",
          borderRadius:"1.5rem",
          background:"rgba(255,255,255,0.04)",
          backdropFilter:"blur(22px) saturate(1.5)",
          WebkitBackdropFilter:"blur(22px) saturate(1.5)",
          border:"1px solid rgba(56,189,248,0.18)",
          boxShadow:"0 10px 60px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)",
          display:"flex", flexDirection:"column", alignItems:"center", gap:"1.8rem",
          animation:"ld-in 0.55s cubic-bezier(.4,0,.2,1) both",
          minWidth:300,
        }}>

          {/* Corner accents */}
          {[
            { top:0, left:0,  borderTop:"2px solid #38BDF8", borderLeft:"2px solid #38BDF8" },
            { top:0, right:0, borderTop:"2px solid #FAA617", borderRight:"2px solid #FAA617" },
            { bottom:0, left:0,  borderBottom:"2px solid #FAA617", borderLeft:"2px solid #FAA617" },
            { bottom:0, right:0, borderBottom:"2px solid #38BDF8", borderRight:"2px solid #38BDF8" },
          ].map((s,i) => (
            <div key={i} style={{ position:"absolute", width:18, height:18, borderRadius:2, ...s }}/>
          ))}

          {/* Logo */}
          <img
            src={logo} srcSet={`${logo} 1x, ${logo2x} 2x`} alt="Logo"
            width="300" height="200" decoding="async"
            style={{ height:84, width:"auto", animation:"ld-float 3s ease-in-out infinite" }}
          />

          {/* Spinner */}
          <div style={{ position:"relative", width:76, height:76 }}>
            {/* outer – blue */}
            <div style={{
              position:"absolute", inset:0, borderRadius:"50%",
              border:"3px solid transparent",
              borderTopColor:"#38BDF8", borderRightColor:"rgba(56,189,248,.25)",
              animation:"ld-cw 1.1s linear infinite",
              boxShadow:"0 0 14px rgba(56,189,248,.7)",
            }}/>
            {/* mid – orange */}
            <div style={{
              position:"absolute", inset:10, borderRadius:"50%",
              border:"3px solid transparent",
              borderBottomColor:"#FAA617", borderLeftColor:"rgba(250,166,23,.25)",
              animation:"ld-ccw .8s linear infinite",
              boxShadow:"0 0 14px rgba(250,166,23,.7)",
            }}/>
            {/* inner – white */}
            <div style={{
              position:"absolute", inset:22, borderRadius:"50%",
              border:"2px solid transparent",
              borderTopColor:"rgba(255,255,255,.55)",
              animation:"ld-cw .5s linear infinite",
            }}/>
            {/* center dot */}
            <div style={{
              position:"absolute", top:"50%", left:"50%",
              transform:"translate(-50%,-50%)",
              width:9, height:9, borderRadius:"50%", background:"#38BDF8",
              boxShadow:"0 0 12px 4px rgba(56,189,248,.8)",
            }}/>
          </div>

          {/* ── Progress bar ───────────────────────────────────────────── */}
          <div style={{ width:"100%" }}>
            {/* track */}
            <div style={{
              position:"relative", width:"100%", height:6,
              borderRadius:99, background:"rgba(255,255,255,0.07)",
              overflow:"hidden",
            }}>
              {/* fill */}
              <div style={{
                position:"absolute", top:0, left:0,
                height:"100%", width:`${progress}%`,
                borderRadius:99,
                background:"linear-gradient(to right,#0D6EFD,#38BDF8,#FAA617)",
                boxShadow:"0 0 10px rgba(56,189,248,.8)",
                animation:"ld-glow 1.2s ease-in-out infinite",
                transition:"width 0.08s linear",
              }}/>
              {/* moving glare dot */}
              <div style={{
                position:"absolute", top:"50%",
                left:`calc(${progress}% - 5px)`,
                transform:"translateY(-50%)",
                width:10, height:10, borderRadius:"50%",
                background:"white",
                boxShadow:"0 0 8px 3px rgba(255,255,255,.9)",
                transition:"left 0.08s linear",
              }}/>
            </div>

            {/* percentage + label */}
            <div style={{
              display:"flex", justifyContent:"space-between",
              alignItems:"center", marginTop:10,
            }}>
              <span style={{
                fontFamily:"Cairo,sans-serif", fontSize:".82rem",
                color:"rgba(255,255,255,.45)", letterSpacing:"0.08em",
              }}>
                {isArabic ? "جاري التحميل" : "LOADING"}
              </span>
              <span style={{
                fontFamily:"monospace", fontSize:"1rem", fontWeight:700,
                color:"#38BDF8",
                textShadow:"0 0 10px rgba(56,189,248,.9)",
              }}>
                {pct}%
              </span>
            </div>
          </div>

        </div>{/* /card */}
      </div>
    </>
  );
}
